import React, { useState, useEffect } from 'react';
import styles from '../styles/DateOfBirthField.module.css';

const DateOfBirthField = ({ value, onChange, required = false, className = '', style = {}, notFuture = true, minYear }) => {
  // Parse existing value if provided (format: YYYY-MM-DD)
  const parseDate = (dateString) => {
    if (!dateString) return { day: '', month: '', year: '' };
    const [year, month, day] = dateString.split('-');
    return {
      day: day ? parseInt(day, 10) : '',
      month: month ? parseInt(month, 10) : '',
      year: year || ''
    };
  };

  const [dateFields, setDateFields] = useState(parseDate(value));
  const [dateError, setDateError] = useState('');

  // Update internal state when external value changes
  useEffect(() => {
    if (value && value !== formatToYYYYMMDD(dateFields)) {
      setDateFields(parseDate(value));
    }
  }, [value]);

  // Format date to YYYY-MM-DD for backend
  const formatToYYYYMMDD = (fields) => {
    if (!fields.day || !fields.month || !fields.year) return '';
    const paddedMonth = String(fields.month).padStart(2, '0');
    const paddedDay = String(fields.day).padStart(2, '0');
    return `${fields.year}-${paddedMonth}-${paddedDay}`;
  };

  // Get number of days in a month
  const getDaysInMonth = (month, year) => {
    if (!month) return 31;
    if (!year) year = 2000; // Use leap year for Feb if year not selected
    return new Date(year, month, 0).getDate();
  };

  // Get current year for validation
  const currentYear = new Date().getFullYear();
  // minYear prop (admin-configured per field) wins when given; otherwise fall back to the
  // original hardcoded 100-years-back floor so existing callers (LandingPage.jsx,
  // LandingPageVariantB.jsx — neither passes minYear) keep their current behavior unchanged.
  const effectiveMinYear = typeof minYear === 'number' ? minYear : currentYear - 100;
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Handle field changes
  const handleFieldChange = (field, value) => {
    const newFields = { ...dateFields, [field]: value };

    // Reset day if it exceeds the new month's max days (handles leap years automatically)
    if (field === 'month' || field === 'year') {
      const maxDays = getDaysInMonth(newFields.month, newFields.year);
      if (newFields.day > maxDays) {
        newFields.day = ''; // Reset day dropdown instead of auto-adjusting
      }
    }

    setDateFields(newFields);

    // Call onChange with formatted date string
    const formattedDate = formatToYYYYMMDD(newFields);
    let finalValue = formattedDate;
    let error = '';

    if (formattedDate) {
      // String comparison is correct here since both sides are zero-padded YYYY-MM-DD —
      // catches a future month/day within the current year, which the year-only clamp in
      // handleYearChange below can't (e.g. year=currentYear, month=Dec, day=31 today).
      if (notFuture && formattedDate > todayStr) {
        error = 'Date cannot be in the future';
        finalValue = '';
      } else if (newFields.year && parseInt(newFields.year, 10) < effectiveMinYear) {
        error = `Year must be ${effectiveMinYear} or later`;
        finalValue = '';
      }
    }

    setDateError(error);

    if (onChange) {
      onChange({ target: { name: 'dob', value: finalValue } });
    }
  };

  // Generate day options (1-31, adjusted based on selected month)
  const maxDays = getDaysInMonth(dateFields.month, dateFields.year);
  const dayOptions = Array.from({ length: maxDays }, (_, i) => i + 1);

  // Month options (with names)
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Handle year input with validation
  const handleYearChange = (e) => {
    let yearValue = e.target.value.replace(/\D/g, ''); // Only digits
    if (yearValue.length > 4) yearValue = yearValue.slice(0, 4);

    // Once a full 4-digit year is typed, clamp it into a sane range: no future years
    // (unless notFuture is explicitly false for this field) and no earlier than
    // effectiveMinYear (admin-configured minYear, or 100-years-back default).
    if (yearValue.length === 4) {
      const parsedYear = parseInt(yearValue, 10);
      if (notFuture && parsedYear > currentYear) {
        yearValue = String(currentYear);
      } else if (parsedYear < effectiveMinYear) {
        yearValue = String(effectiveMinYear);
      }
    }

    handleFieldChange('year', yearValue);
  };

  return (
    <div className={`${styles.dobContainer} ${className}`} style={style}>
      <div className={styles.dobFields}>
        <div className={styles.dobField}>
          <select
            value={dateFields.day}
            onChange={(e) => handleFieldChange('day', e.target.value ? parseInt(e.target.value, 10) : '')}
            className={styles.dobSelect}
            required={required}
            aria-label="Day"
          >
            <option value="">Day</option>
            {dayOptions.map(day => (
              <option key={day} value={day}>{day}</option>
            ))}
          </select>
        </div>

        <div className={styles.dobField}>
          <select
            value={dateFields.month}
            onChange={(e) => handleFieldChange('month', e.target.value ? parseInt(e.target.value, 10) : '')}
            className={styles.dobSelect}
            required={required}
            aria-label="Month"
          >
            <option value="">Month</option>
            {monthOptions.map(month => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </select>
        </div>

        <div className={styles.dobField}>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={dateFields.year}
            onChange={handleYearChange}
            placeholder="YYYY"
            className={styles.dobInput}
            required={required}
            maxLength={4}
            aria-label="Year"
          />
        </div>
      </div>
      
      {dateError ? (
        <div style={{ color: '#ff3d00', fontSize: 13, marginTop: 4 }}>{dateError}</div>
      ) : (
        dateFields.day && dateFields.month && dateFields.year && (
          <div className={styles.dobPreview}>
            Selected: {dateFields.day} {monthOptions[dateFields.month - 1]?.label} {dateFields.year}
          </div>
        )
      )}
    </div>
  );
};

export default DateOfBirthField;
